import React, { useEffect, useState } from 'react';
import { DayPicker, Matcher } from 'react-day-picker';
import 'react-day-picker/style.css';
import { useCheckTimesSlotAvailability } from '../../../hook/useMutationData';

import moment from 'moment';

import { AvailabilityGet, ServiceGet, TimeSlotGet } from '../../../utils/type';
import { FaRegCalendarCheck } from 'react-icons/fa6';
import { addToast } from '@heroui/react';
import {
  ANY_STAFF_ID,
  ANY_STAFF_LABEL,
  getBookingAvailabilityMessage,
  handlingPassedTime,
} from '../../../utils/common';
import { useTranslation } from 'react-i18next';

type DateAndStaffProps = {
  data: ServiceGet;
  onChange: (data: ITimeAndStaff) => void;
  /** Exposes internal state so parent can render time slots/staff externally */
  onStateChange?: (state: DateAndStaffExposedState) => void;
};

interface DateAndStaffExposedState {
  timeSlots: TimeSlotGet[];
  selectTimeSlot: string;
  setSelectTimeSlot: (val: string) => void;
  availableStaff: StaffMember[] | null;
  selectedStaffId: string;
  setSelectedStaffId: (val: string) => void;
  isPending: boolean;
  isCheckTimeSlotError: boolean;
  checkTimeSlotError: any;
  availabilityMessage: string | null;
  selected: Date | undefined;
}

interface ITimeAndStaff {
  date: string;
  timeSlotId: string;
  time: string;
  staffId: string;
  staff: StaffMember;
  start: string;
  end: string;
  providerTimeZone?: string;
}

type weekType = {
  day: string;
  key: number;
};

type StaffMember = {
  staffId: string;
  fullName: string;
  providerStaffId: string;
  isProvider: boolean;
};

const ANY_STAFF_MEMBER: StaffMember = {
  staffId: ANY_STAFF_ID,
  fullName: ANY_STAFF_LABEL,
  providerStaffId: '',
  isProvider: false,
};

// The calendar only ever offers today .. +2 months (see `endMonth`), so any
// forward search for a bookable day is bounded by that same window.
const BOOKING_WINDOW_DAYS = 62;

const week: weekType[] = [
  { day: 'sunday', key: 0 },
  { day: 'monday', key: 1 },
  { day: 'tuesday', key: 2 },
  { day: 'wednesday', key: 3 },
  { day: 'thursday', key: 4 },
  { day: 'friday', key: 5 },
  { day: 'saturday', key: 6 },
];

const DateAndStaff = ({ data, onChange, onStateChange }: DateAndStaffProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<Date>();
  const [unAvailableDates, setUnAvailableDates] = useState<number[]>([0]);
  const [timeSlots, setTimeSlots] = useState<TimeSlotGet[]>([]);
  const [selectTimeSlot, setSelectTimeSlot] = useState<string>('');
  const [selectedStaffId, setSelectedStaffId] = useState<string>('');
  const [availableStaff, setAvailableStaff] = useState<StaffMember[] | null>(
    null
  );
  const [availabilityMessage, setAvailabilityMessage] = useState<string | null>(
    null
  );

  //ASSUMENT THIS API DATA
  // const holiday = [new Date(2025, 8, 23), new Date(2025, 9, 14)];

  const {
    mutateAsync,
    isPending,
    isError: isCheckTimeslotError,
    error: checkTimeslotError,
  } = useCheckTimesSlotAvailability();

  const endMonth = new Date();
  endMonth.setMonth(endMonth.getMonth() + 2);
  const today = new Date();

  // CHECK AVAILABILITY TIMESLOT
  useEffect(() => {
    const checkBookingAvailableTimeSlot = async () => {
      const serviceId = data?.serviceId;

      if (!serviceId) return;
      // An Invalid Date is still a truthy object, so `!selected` alone would let
      // one through and post the literal string "Invalid date" as the date.
      if (!selected || !moment(selected).isValid()) {
        console.error('No valid date selected');
        return;
      }

      setSelectTimeSlot('');
      setAvailableStaff(null);
      setSelectedStaffId('');
      setAvailabilityMessage(null);

      const payload = {
        serviceId,
        date: moment(selected).format('YYYY-MM-DD'),
      };

      try {
        const response = await mutateAsync(payload);
        console.log('RESPONSE: ', response);
        const availabilityError = getBookingAvailabilityMessage(response);
        setAvailabilityMessage(availabilityError);

        if (!response?.success) {
          console.error('Error check times-lot availability:', response);
          addToast({
            title: t('booking.error_timeslot'),
            description:
              availabilityError ||
              response?.message ||
              t('booking.timeslot_error_desc'),
            radius: 'md',
            color: 'danger',
          });
        }

        const newTimeSlots = response?.timeSlots || [];
        const providerTimeZone = 'America/Toronto';

        const result: any = await handlingPassedTime(
          newTimeSlots,
          providerTimeZone,
          selected
        );

        if (result?.length !== 0) {
          setTimeSlots(result);
          setSelectTimeSlot(result?.[0]?.timeSlotId || '');
        } else {
          setTimeSlots([]);
          setSelectTimeSlot('');
        }
      } catch (error) {
        console.error('Error check time-slot availability: ', error);
        setTimeSlots([]);
      }
    };

    checkBookingAvailableTimeSlot();
  }, [mutateAsync, data?.serviceId, selected]);

  // STAFF
  useEffect(() => {
    try {
      setSelectedStaffId('');
      const matchedSlot = timeSlots.find(
        (slot: TimeSlotGet) => slot.timeSlotId === selectTimeSlot
      );

      // matchedSlot may be a timeslot object that contains an availableStaff array
      const staffList = matchedSlot?.availableStaff || [];
      setAvailableStaff(staffList);
      setSelectedStaffId(
        staffList.length > 1 ? ANY_STAFF_ID : staffList[0]?.staffId || ''
      );
    } catch (error) {
      console.error('Error filter staff based on time slot: ', error);
    }
  }, [timeSlots, selectTimeSlot]);

  // find the next available date, or null when the service has no bookable day
  // inside the calendar window.
  //
  // The search MUST be bounded. Unbounded, a service whose every weekday is
  // blocked walks the date forward ~100M times until it overflows past the max
  // JS date, at which point getDay() returns NaN, `blockedWeekdays.includes(NaN)`
  // is false, and the loop returns an *Invalid Date*. That poisoned value then
  // reaches the availability API as the literal string "Invalid date" and blocks
  // booking entirely. Bounding to the calendar window makes the impossible case
  // return null instead, which callers handle explicitly.
  const getNextAvailableDate = (
    startDate: Date,
    blockedWeekdays: number[]
  ): Date | null => {
    const date = new Date(startDate);
    for (let i = 0; i <= BOOKING_WINDOW_DAYS; i++) {
      if (!blockedWeekdays.includes(date.getDay())) {
        return date;
      }
      date.setDate(date.getDate() + 1);
    }
    return null;
  };

  //HANDLE UN AVAILABLE DATES
  useEffect(() => {
    console.log('unavailable dates');
    // MISSING availability (still loading, or a refetch briefly returning
    // nothing) must leave state untouched — clearing it here would blank the
    // calendar and wipe a date the customer had already picked.
    if (!data?.availability) return;

    // An EMPTY array is a different case, and is NOT caught by the guard above
    // because `[]` is truthy. It used to fall through, mark all seven weekdays
    // blocked, and ask getNextAvailableDate for a day that cannot exist. Treat
    // "no days configured" as an explicit no-bookable-dates state.
    if (!Array.isArray(data.availability) || data.availability.length === 0) {
      setUnAvailableDates(week.map((w) => w.key));
      setSelected(undefined);
      return;
    }

    const availability = data?.availability;

    const availableKeys = availability.map((a: AvailabilityGet) => {
      return week.find((w: weekType) => w.day === a.day)?.key;
    });

    const unAvailableKeys = week
      .map((w) => w.key)
      .filter((key) => !availableKeys.includes(key));

    setUnAvailableDates(unAvailableKeys);

    const nextDate = getNextAvailableDate(today, unAvailableKeys);
    setSelected(nextDate ?? undefined);
  }, [data]);

  useEffect(() => {
    // Find the selected staff member
    const getStaff =
      selectedStaffId === ANY_STAFF_ID
        ? ANY_STAFF_MEMBER
        : availableStaff?.find((s: StaffMember) => {
            return s.staffId === selectedStaffId;
          });

    // Find the selected time slot
    const timeSlot = timeSlots?.find((s: TimeSlotGet) => {
      return s.timeSlotId === selectTimeSlot;
    });

    if (getStaff && timeSlot && selected) {
      const providerTimeZone = 'America/Toronto';
      onChange({
        date: moment(selected).format('YYYY-MM-DD'),
        timeSlotId: selectTimeSlot,
        staffId: selectedStaffId,
        time: `${timeSlot.from} - ${timeSlot.to}`,
        staff: getStaff,
        start: timeSlot.from,
        end: timeSlot.to,
        providerTimeZone,
      });
    }
  }, [
    selected,
    selectTimeSlot,
    selectedStaffId,
    onChange,
    availableStaff,
    timeSlots,
  ]);

  // Expose internal state to parent for external rendering
  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        timeSlots,
        selectTimeSlot,
        setSelectTimeSlot,
        availableStaff,
        selectedStaffId,
        setSelectedStaffId,
        isPending,
        isCheckTimeSlotError: isCheckTimeslotError,
        checkTimeSlotError: checkTimeslotError,
        availabilityMessage,
        selected,
      });
    }
  }, [
    timeSlots,
    selectTimeSlot,
    availableStaff,
    selectedStaffId,
    isPending,
    isCheckTimeslotError,
    checkTimeslotError,
    availabilityMessage,
    selected,
    onStateChange,
  ]);

  const holidays: Date[] = [
    //  new Date("2026-05-22T00:00:00"), // Uses standard calendar months (05 = May)
    //   new Date("2026-05-23T00:00:00"),
    //   new Date("2026-05-24T00:00:00"),
  ];
  const disable: Matcher[] = [
    { before: today },
    { dayOfWeek: unAvailableDates },
    ...holidays,
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-3">
      <div className="pb-2 mb-3 border-b border-gray-100 flex flex-wrap justify-between items-center gap-2">
        <p className="text-title text-[15px] font-semibold">
          {t('booking.choose_date_time')}
        </p>
        <p className="text-body-bold flex items-center gap-2">
          <FaRegCalendarCheck className="text-secondary" />
          {selected && moment(selected).isValid()
            ? moment(selected).format('DD MMM YYYY')
            : t('booking.no_date_available', 'No dates available')}
        </p>
      </div>

      <div
        className="rounded-xl border-none flex justify-center overflow-x-auto text-xs [&_.rdp-month_caption]:text-xs [&_.rdp-month_caption]:font-semibold [&_.rdp-weekday]:text-[10px]"
        style={
          {
            '--rdp-day-width': '30px',
            '--rdp-day-height': '30px',
            '--rdp-day_button-width': '28px',
            '--rdp-day_button-height': '28px',
            '--rdp-nav_button-width': '4px',
            '--rdp-nav_button-height': '24px',
            '--rdp-nav-height': '28px',
            '--rdp-weekday-padding': '0.25rem 0',
            '--rdp-accent-color': 'hsl(var(--secondary))',
            '--rdp-accent-background-color': 'hsl(var(--secondary) / 0.1)',
          } as React.CSSProperties
        }
      >
        <DayPicker
          mode="single"
          animate
          navLayout="around"
          modifiersClassNames={{
            selected: 'rounded-full bg-secondary text-white',
          }}
          defaultMonth={today}
          disabled={disable}
          startMonth={today}
          endMonth={endMonth}
          selected={selected}
          onSelect={setSelected}
          required
        />
      </div>
    </div>
  );
};

export default DateAndStaff;
